import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { User, Mail, Phone, MapPin, AlertTriangle, Building2, Briefcase, Users, Save } from "lucide-react";
import api from "../../lib/api";

const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get("/self-service/me");
      setProfile(res.data);
      setForm({
        phone: res.data.phone || "",
        address: res.data.address || "",
        emergency_contact: res.data.emergency_contact || "",
        birthday: res.data.birthday || "",
        anniversary_date: res.data.anniversary_date || "",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put("/self-service/me", form);
      toast.success("Profile updated");
      setEditing(false);
      fetchProfile();
    } catch {
      toast.error("Failed to update profile");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!profile) {
    return <div className="text-center py-20 text-slate-500">No profile found. Contact your admin.</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="my-profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">My Profile</h1>
          <p className="text-slate-500">View and edit your personal information</p>
        </div>
        {!editing ? (
          <Button className="btn-primary" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">Cancel</Button>
            <Button className="btn-primary" onClick={handleSave} data-testid="save-profile-btn">
              <Save className="h-4 w-4 mr-2" /> Save
            </Button>
          </div>
        )}
      </div>

      {/* Identity Card */}
      <Card className="card-soft">
        <CardContent className="p-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-bold text-primary">
                {profile.first_name?.charAt(0)}{profile.last_name?.charAt(0)}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-800">{profile.first_name} {profile.last_name}</h2>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {profile.employee_code && (
                  <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">{profile.employee_code}</Badge>
                )}
                {profile.designation_name && (
                  <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">{profile.designation_name}</Badge>
                )}
                {profile.department_name && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">{profile.department_name}</Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Read-only Info */}
      <Card className="card-soft">
        <CardHeader><CardTitle className="font-heading text-base">Work Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <Mail className="h-4 w-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-400">Email</p>
              <p className="text-sm text-slate-800">{profile.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Building2 className="h-4 w-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-400">Department</p>
              <p className="text-sm text-slate-800">{profile.department_name || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Briefcase className="h-4 w-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-400">Designation</p>
              <p className="text-sm text-slate-800">{profile.designation_name || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Users className="h-4 w-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-400">Reports To</p>
              <p className="text-sm text-slate-800">{profile.manager_name || "—"}</p>
            </div>
          </div>
          {profile.hire_date && (
            <div className="flex items-center gap-3">
              <User className="h-4 w-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-400">Hire Date</p>
                <p className="text-sm text-slate-800">{profile.hire_date}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editable Personal Info */}
      <Card className="card-soft">
        <CardHeader><CardTitle className="font-heading text-base">Personal Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" /> Phone</Label>
              {editing ? (
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  className="input-field" data-testid="phone-input" />
              ) : (
                <p className="text-sm text-slate-800 py-2">{profile.phone || "—"}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-slate-400" /> Emergency Contact</Label>
              {editing ? (
                <Input value={form.emergency_contact} onChange={e => setForm(p => ({ ...p, emergency_contact: e.target.value }))}
                  className="input-field" data-testid="emergency-input" />
              ) : (
                <p className="text-sm text-slate-800 py-2">{profile.emergency_contact || "—"}</p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" /> Address</Label>
            {editing ? (
              <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                className="input-field" data-testid="address-input" />
            ) : (
              <p className="text-sm text-slate-800 py-2">{profile.address || "—"}</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Birthday</Label>
              {editing ? (
                <Input type="date" value={form.birthday} onChange={e => setForm(p => ({ ...p, birthday: e.target.value }))}
                  className="input-field" data-testid="birthday-input" />
              ) : (
                <p className="text-sm text-slate-800 py-2">{profile.birthday || "—"}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Anniversary Date</Label>
              {editing ? (
                <Input type="date" value={form.anniversary_date} onChange={e => setForm(p => ({ ...p, anniversary_date: e.target.value }))}
                  className="input-field" data-testid="anniversary-input" />
              ) : (
                <p className="text-sm text-slate-800 py-2">{profile.anniversary_date || "—"}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyProfile;
